import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import { useCreateStoryboard } from "@/hooks/storyboards/useCreateStoryboard";
import {
  useActiveCanvas,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { useElementMetadata } from "@/components/contexts/ElementMetadataContext";
import { getStoriesForComponent } from "@/utils/jsx/extractElementsUtils";
import { getImageUrlForElement } from "@/components/Canvas/utils";
import type { Storyboard } from "@/utils/types";
import { ReactFramework } from "@/utils/templateProjects";
import { useDatabaseSyncWrapperStore } from "@/components/contexts/databaseSyncStore";
import { useLoadCodebase } from "@/utils/devServerUtils";

export const useMaybeInitializeSubcanvasStoryboards = () => {
  const dispatch = useDispatch<any>();
  const activeCanvas = useActiveCanvas();
  const mainCanvas = useActiveMainCanvas();
  const project = useActiveProject();
  const canvasId = useCanvasId();
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const triggerRefetchProjectLoadingProgress = useDatabaseSyncWrapperStore(
    (state) => state.triggerRefetchProjectLoadingProgress,
  );
  const { metadata } = useElementMetadata();

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey,
  );
  const { componentKeyToComponent } = useAssetComponents();
  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );

  const gitStatus = useSelector((state: any) => state.gitStatus.gitStatus);
  const subCanvasesLoaded = useSupabaseStore(
    (state) => state.subCanvasesStatus === "succeeded",
  );

  const loadCodebase = useLoadCodebase();

  useEffect(() => {
    // Wait for git to load as well
    if (!gitStatus) {
      return;
    }

    if (!canvasComponentKey) {
      return;
    }

    // We need to wait for subCanvases to be loaded from the database
    // This ensures we can check if a subcanvas already exists before creating a new one
    if (!subCanvasesLoaded) {
      return;
    }

    if (!project) {
      return;
    }

    const updateOrCreateStoryboardsForComponent = async () => {
      // Need to wait for canvas to be created first
      if (!mainCanvas || !supabaseClient) {
        return;
      }

      let canvasToUpdate: any;
      let isNewCanvas = false;

      if (activeCanvas) {
        canvasToUpdate = {
          ...activeCanvas,
        };
      } else {
        // Create a new canvas
        canvasToUpdate = {
          id: uuidv4().toString(),
          type: "COMPONENT",
          canvas_id: canvasId,
          component_id: canvasComponentKey,
          storyboards: [],
        };

        isNewCanvas = true;
      }

      const component = componentKeyToComponent[canvasComponentKey];

      const stories = getStoriesForComponent(
        {
          framework: project?.framework as ReactFramework,
        },
        treeElementLookup,
        component,
      );

      const storyboardsForStoryTypes = canvasToUpdate.storyboards.filter(
        (storyboard) => {
          return (
            storyboard.type === STORYBOARD_TYPES.STORY &&
            storyboard.deleted !== true &&
            storyboard.renderStatus !== "placeholder"
          );
        },
      );

      let needToUpdate: boolean = false;
      storyboardsForStoryTypes.forEach((storyboard) => {
        const matchingStory = stories.find(
          (story) => storyboard.name === story.componentName,
        );

        // Allow 10 seconds for the tree elements to refresh
        if (storyboard.recentlySavedTs) {
          if (
            matchingStory ||
            Date.now() - Date.parse(storyboard.recentlySavedTs) >= 1000 * 10
          ) {
            // It has been found, clear the recentlySavedTs state
            storyboard.recentlySavedTs = null;
            needToUpdate = true;
          }
          return;
        }

        if (!matchingStory) {
          storyboard.deleted = true;
          storyboard.deleted_at = new Date().toISOString();
          needToUpdate = true;
          return;
        }

        if (storyboard.storyCodebaseId !== matchingStory.codebaseId) {
          storyboard.storyCodebaseId = matchingStory.codebaseId;
          needToUpdate = true;
        }
      });

      const storiesWithoutStoryboards = stories.filter((story) => {
        return !storyboardsForStoryTypes.find(
          (storyboard) => storyboard.name === story?.componentName,
        );
      });

      // Nothing to update
      if (!storiesWithoutStoryboards.length && !isNewCanvas && !needToUpdate) {
        return;
      }

      const storyIdToSize = {};
      await Promise.all(
        storiesWithoutStoryboards.map(async (story) => {
          const imageUrl = getImageUrlForElement(metadata, story);
          if (!imageUrl) {
            return;
          }

          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = imageUrl;

          const imageLoadPromise = new Promise<{
            width?: number;
            height?: number;
          }>((resolve, reject) => {
            img.onload = () => {
              resolve({
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
            };

            img.onerror = (
              e,
              source?: string,
              lineno?: number,
              colno?: number,
              error?: Error,
            ) => {
              console.error("ERROR", e, error);
              resolve({});
            };
          });

          const { width, height } = await imageLoadPromise;

          if (width !== undefined && height !== undefined) {
            storyIdToSize[story.codebaseId] = {
              width: Math.max(width, 16),
              height: Math.max(height, 16),
            };
          }
        }),
      );

      let currentX = 0;
      let averageWidth = 0;
      let averageHeight = 0;
      let totalNum = 0;

      canvasToUpdate.storyboards.forEach((sb) => {
        if (sb.deleted || sb.type !== STORYBOARD_TYPES.STORY) {
          return;
        }

        currentX = Math.max(sb.x + sb.width + 100, currentX);
        averageWidth += sb.width;
        averageHeight += sb.height;
        totalNum += 1;
      });

      Object.values(storyIdToSize).forEach(({ width, height }: any) => {
        averageWidth += width;
        averageHeight += height;
        totalNum += 1;
      });

      if (!totalNum) {
        // Case where there are no storyboards and there are no images for those storyboards
        averageWidth = 500;
        averageHeight = 500;
      } else {
        averageWidth = Math.round(averageWidth / totalNum);
        averageHeight = Math.round(averageHeight / totalNum);
      }

      canvasToUpdate.storyboards = canvasToUpdate.storyboards.concat(
        storiesWithoutStoryboards.map((story, index): Storyboard => {
          const { width, height } = storyIdToSize[story.codebaseId] || {};

          const widthToUse = width || averageWidth;
          const heightToUse = height || averageHeight;

          const toReturn: Storyboard = {
            id: uuidv4().toString(),
            name: story.componentName,
            type: STORYBOARD_TYPES.STORY,
            width: widthToUse,
            height: heightToUse,
            x: currentX,
            y: -heightToUse / 2,
            zIndex: 0,
            saved: true,
            created: true,
            created_at: new Date(),
            fullFilePath: story.filename,
            storySourceComponentFilename:
              story.storybookMainComponentImport?.filename,
            storyUniqueIdentifier: `${story.filename}_${story.componentName}`,
            storyCodebaseId: story.codebaseId,
            resizeOnceRendered: true,
          };

          currentX += widthToUse + 100;

          return toReturn;
        }),
      );
      canvasToUpdate.last_updated = new Date();

      // Immediately update the local store
      useSupabaseStore
        .getState()
        .onDbSnapshot("subCanvases", { newItems: [canvasToUpdate] });

      if (isNewCanvas) {
        await supabaseClient.from("sub_canvases").insert([canvasToUpdate]);
      } else {
        await supabaseClient
          .from("sub_canvases")
          .update({
            storyboards: canvasToUpdate.storyboards,
            last_updated: canvasToUpdate.last_updated,
          })
          .eq("id", canvasToUpdate.id);
      }

      // Trigger a load to ensure tempobook/index is updated
      await loadCodebase({
        filesToLoad: storiesWithoutStoryboards.map((story) => story.filename),
      });
    };

    updateOrCreateStoryboardsForComponent();
  }, [
    gitStatus,
    canvasId,
    subCanvasesLoaded,
    activeCanvas?.id,
    mainCanvas,
    canvasComponentKey,
    treeElementLookup,
    project,
    supabaseClient,
    dispatch,
    triggerRefetchProjectLoadingProgress,
    metadata,
    componentKeyToComponent,
    mainCanvas,
    project,
  ]);
};
